import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  AfterViewInit,
} from '@angular/core'
import { AuthService } from '@hmp'
import {
  isMigrationGuardElementVisible,
  MIGRATION_GUARD_SELECTOR,
} from './migration-guard.util'

/** `progress` = migrating (1); `v2` = on Version 2 notice (2). */
export type MigrationDialogStatus = 'progress' | 'v2'

/**
 * Full-screen overlay. DOM/CSS tamper guard + forced logout while migrating (`progress`).
 * App-level guard in `app.component` provides a second independent lock layer.
 */
@Component({
  selector: 'app-migration-dialog',
  templateUrl: './migration-dialog.component.html',
  styleUrls: ['./migration-dialog.component.scss'],
})
export class MigrationDialogComponent
  implements AfterViewInit, OnChanges, OnDestroy {
  @Input() lockEnforced = true
  @Input() status: MigrationDialogStatus = 'progress'
  @Input() progress = 45

  @Output() goNewPlatform = new EventEmitter<void>()

  private checkTimer: ReturnType<typeof setInterval> | null = null
  private mutationObserver: MutationObserver | null = null
  private didForceLogout = false

  constructor(
    private el: ElementRef<HTMLElement>,
    private auth: AuthService
  ) {}

  ngAfterViewInit(): void {
    this.restartGuard()
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['lockEnforced'] || changes['status']) {
      this.restartGuard()
    }
  }

  ngOnDestroy(): void {
    this.stopGuard()
  }

  private restartGuard(): void {
    this.stopGuard()
    if (!this.lockEnforced || this.status !== 'progress') {
      return
    }
    this.verifyDomIntact()
    this.checkTimer = setInterval(() => this.verifyDomIntact(), 100)
    this.mutationObserver = new MutationObserver(() => this.verifyDomIntact())
    const host = this.el.nativeElement
    this.mutationObserver.observe(host, {
      attributes: true,
      attributeFilter: ['class', 'style', 'hidden', 'aria-hidden'],
      subtree: true,
    })
    if (document.documentElement) {
      this.mutationObserver.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['class', 'style'],
        subtree: false,
      })
    }
  }

  private stopGuard(): void {
    if (this.checkTimer) {
      clearInterval(this.checkTimer)
      this.checkTimer = null
    }
    if (this.mutationObserver) {
      this.mutationObserver.disconnect()
      this.mutationObserver = null
    }
  }

  private verifyDomIntact(): void {
    if (!this.lockEnforced || this.status !== 'progress' || this.didForceLogout) {
      return
    }
    const host = this.el.nativeElement
    if (!host || !host.isConnected || !isMigrationGuardElementVisible(host)) {
      this.performLogout()
      return
    }
    const overlay = host.querySelector(MIGRATION_GUARD_SELECTOR)
    if (!overlay || !isMigrationGuardElementVisible(overlay)) {
      this.performLogout()
    }
  }

  private performLogout(): void {
    if (this.didForceLogout) {
      return
    }
    this.didForceLogout = true
    this.stopGuard()
    this.auth.logout('login', true)
  }

  onForceLogoutClick(): void {
    this.performLogout()
  }

  goToNewPlatform(): void {
    this.goNewPlatform.emit()
  }
}
